import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import TableCompo from "../Table/TableCompo";
import { toast } from "react-toastify";
import {
  Box,
  Typography,
  CardContent,
  Tooltip,
  TextField,
  Button,
  useTheme,
  TableBody,
  useMediaQuery,
} from "@mui/material";
import {
  exportInactiveCustomerRequest,
  fetchInactiveCustomerListRequest,
} from "../../../redux/customer/CustomerSlice";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Stack } from "@mui/material";
import { getAppConfig } from "../../../redux/Configuration/configurationSlice";

const InactiveCustomer = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();
  const {
    inactiveCustomers = [],
    totalCount = 0,
    loading,
  } = useSelector((state) => state.customer);

  const [pagination, setPagination] = useState({ page: 0, limit: 10 });
  const [search, setSearch] = useState("");
  const [months, setMonths] = useState(1);
  const [isSubmittingDate, setIsSubmittingDate] = useState(false);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const package_name = useSelector(
    (state) => state?.appConfig?.configData.package_name || "",
  );
  console.log("package_name", package_name);
  // useEffect(() => {
  //   dispatch(getAppConfig());
  // }, []);
  useEffect(() => {
    const base = {
      start: pagination.page * pagination.limit,
      limit: pagination.limit,
      search,
    };
    // When both dates are set (after Get Details), keep them on pagination/search changes
    if (startDate && endDate) {
      dispatch(
        fetchInactiveCustomerListRequest({
          ...base,
          startDate,
          endDate,
        }),
      );
    } else {
      dispatch(fetchInactiveCustomerListRequest(base));
    }
  }, [dispatch, pagination, search, startDate, endDate]);

  const handleDateSubmit = () => {
    // Trim the search text
    const trimmedSearch = (search || "").trim();

    if (!startDate || !endDate) {
      toast.error("Please select both start and end dates.");
      return;
    }

    if (startDate > endDate) {
      toast.error("Start date cannot be after end date.");
      return;
    }

    setIsSubmittingDate(true); // start loader
    setPagination((prev) => ({ ...prev, page: 0 }));

    dispatch(
      fetchInactiveCustomerListRequest({
        start: 0,
        limit: pagination.limit,
        search: trimmedSearch,
        startDate,
        endDate,
      }),
    );

    // Simulate API delay
    setTimeout(() => {
      setIsSubmittingDate(false); // stop loader
      toast.success("Customer list filtered successfully.");
    }, 2000);
  };
  const handleTableChange = (action, tableState) => {
    switch (action) {
      case "changePage":
        setPagination((prev) => ({ ...prev, page: tableState.page }));
        break;

      case "changeRowsPerPage":
        setPagination({ page: 0, limit: tableState.rowsPerPage });
        break;

      case "search":
        setSearch(tableState.searchText || "");
        setPagination((prev) => ({ ...prev, page: 0 }));
        break;

      default:
        break;
    }
  };

  const columns = [
    {
      field: "firstName",
      headerName: "Name",
      render: (val, row) =>
        row.customer
          ? `${row.customer.firstName || ""} ${row.customer.middleName || ""} ${row.customer.lastName || ""}`
          : "N.A",
    },
    {
      field: "phone",
      headerName: "Phone",
      render: (val, row) => row.customer?.phone || "N.A",
    },
    {
      field: "email",
      headerName: "Email",
      render: (val, row) => row.customer?.email || "N.A",
    },
    {
      field: "createdAt",
      headerName: "Registration Date",
      render: (val, row) => {
        if (!row.customer?.createdAt) return "N.A";
        const date = new Date(row.customer.createdAt);
        return (
          <Tooltip
            title={date.toLocaleString("en-IN", {
              day: "2-digit",
              month: "short",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            })}
            arrow
          >
            <span>
              {date.toLocaleString("en-IN", {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
              })}
            </span>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <Box sx={{ width: "100%", height: "100%", overflow: "auto" }}>
      <Box
        sx={{
          width: "100%",
          mt: 0,
          background: "#fff",
          borderRadius: "16px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <Typography fontWeight={700} fontSize={24} p={2} fontFamily={"Aeonik"}>
          Inactive Customer List
        </Typography>

        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box
            display="flex"
            flexDirection={isMobile ? "column" : "row"}
            gap={2}
            p={2}
          >
            <DatePicker
              label="Select Start Date"
              value={startDate}
              onChange={setStartDate}
              sx={{ minWidth: { xs: "160px", sm: "300px" } }}
              renderInput={(params) => <TextField size="small" {...params} />}
            />
            <DatePicker
              label="Select End Date"
              value={endDate}
              onChange={setEndDate}
              sx={{ minWidth: { xs: "160px", sm: "300px" } }}
              renderInput={(params) => (
                <TextField size="small" {...params} fullWidth />
              )}
            />
            <Button
              variant="contained"
              fullWidth={isMobile}
              sx={{
                backgroundColor: "#752ADA",
                whiteSpace: "nowrap",
                textTransform: "none",
                px: 6,
                fontSize: "16px",
                mt: isMobile ? 1 : 0,
                minWidth: { sx: "80px", sm: "70px" },
              }}
              onClick={handleDateSubmit}
              disabled={isSubmittingDate}
            >
              {isSubmittingDate ? "Processing..." : "Get Details"}
            </Button>
          </Box>
        </LocalizationProvider>

        <CardContent sx={{ height: "100%", width: "100%" }}>
          <TableCompo
            columns={columns}
            data={JSON.parse(JSON.stringify(inactiveCustomers))}
            totalCount={totalCount}
            onTableChange={handleTableChange}
            page={pagination.page}
            limit={pagination.limit}
            extraButtons={
              <Stack direction="row" spacing={2}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() =>
                    dispatch(
                      exportInactiveCustomerRequest({
                        startDate,
                        endDate,
                      }),
                    )
                  }
                >
                  Extract CSV
                </Button>

                <Button
                  size="small"
                  variant="outlined"
                  component="a"
                  href={`${
                    process.env.REACT_APP_API_BASE_URL
                  }/static_data/${package_name}/inactive_customers.csv?nocache=${Date.now()}`}
                  download="inactive_customers.csv"
                >
                  Download
                </Button>
              </Stack>
            }
          />
        </CardContent>
      </Box>
    </Box>
  );
};

export default InactiveCustomer;
